// Inline-TVF body prefix stripper (Slice 19 helper for indexBuilder).
//
// Inline TVFs ship as full `CREATE FUNCTION` source per dialect:
//   MSSQL:  `CREATE FUNCTION fn(...) RETURNS TABLE AS RETURN (SELECT ...)`
//   PG:     `CREATE OR REPLACE FUNCTION fn(...) RETURNS TABLE(...) LANGUAGE sql AS $tag$ SELECT ... $tag$`
//
// `stripInlineTvfPrefix` returns the inner SELECT source plus the byte
// offset to add back when re-deriving line/column ranges, mirroring the
// shape of `stripCreateViewPrefix` in `indexBuilder.ts`. Mostly engine-
// agnostic above the prefix grammar — the rest of the pipeline (parse,
// walk, column-scanner) is reused unchanged.

import type { SqlEngine } from '../../sql-grammar/model';

export function stripInlineTvfPrefix(
  body: string,
  engine: SqlEngine,
): { selectSource: string; byteOffset: number } {
  if (engine === 'postgres') return stripPgDollarBody(body);
  if (engine === 'mssql') return stripMssqlReturnBody(body);
  return { selectSource: body, byteOffset: 0 };
}

/** PG `pg_get_functiondef` always normalizes the body delimiter to `$function$`,
 *  but other engines / older dumps may use any `$tag$` (`$$`, `$body$`, …).
 *  Find the first `AS <tag>` and the matching close `<tag>`; return the slice
 *  in between. The space-preserving boundary keeps offsets aligned with the
 *  original body, so `viewWalker.emitRef`'s `byteOffset` adjustment is enough
 *  to map ranges back to the original `bodyText`. */
function stripPgDollarBody(body: string): { selectSource: string; byteOffset: number } {
  const m = /\bAS\s+(\$[A-Za-z0-9_]*\$)/i.exec(body);
  if (!m) return { selectSource: body, byteOffset: 0 };
  const tag = m[1];
  const innerStart = m.index + m[0].length;
  const closeIdx = body.indexOf(tag, innerStart);
  if (closeIdx < 0) return { selectSource: body, byteOffset: 0 };
  return { selectSource: body.slice(innerStart, closeIdx), byteOffset: innerStart };
}

/** MSSQL inline TVFs have shape `CREATE FUNCTION ... RETURNS TABLE AS RETURN (SELECT ...)`.
 *  The wrapping parens around the SELECT are optional. We strip the header up
 *  to and including `RETURN`, and replace any wrapping `(`/`)` with spaces so
 *  the inner SELECT parses cleanly while byte offsets stay aligned with the
 *  original body. */
function stripMssqlReturnBody(body: string): { selectSource: string; byteOffset: number } {
  const m = /\bRETURNS\s+TABLE\b[\s\S]*?\bRETURN\s*/i.exec(body);
  if (!m) return { selectSource: body, byteOffset: 0 };
  const offset = m.index + m[0].length;
  const tail = body.slice(offset);
  // Replace the FIRST `(` after RETURN (if any) and its matching `)` with
  // spaces. This keeps the inner SELECT byte-aligned with the source and
  // removes wrapping parens that would otherwise force the parser to treat
  // the body as a derived-table fragment.
  const leading = tail.match(/^\s*\(/);
  if (!leading) return { selectSource: tail, byteOffset: offset };
  const openIdx = leading[0].length - 1;
  const closeIdx = matchClosingParen(tail, openIdx);
  if (closeIdx < 0) return { selectSource: tail, byteOffset: offset };
  const chars = tail.split('');
  chars[openIdx] = ' ';
  chars[closeIdx] = ' ';
  return { selectSource: chars.join(''), byteOffset: offset };
}

function matchClosingParen(body: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < body.length; i++) {
    const c = body[i];
    if (c === '(') depth += 1;
    else if (c === ')') {
      depth -= 1;
      if (depth === 0) return i;
    } else if (c === "'") {
      i = body.indexOf("'", i + 1);
      if (i < 0) return -1;
    }
  }
  return -1;
}
